import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import MDEditor from "@uiw/react-md-editor";
import {
  Save,
  Rocket,
  Undo2,
  Trash2,
  Plus,
  ChevronRight,
  AlertCircle,
  CircleCheck,
  Globe2,
  Loader2,
} from "lucide-react";

const SCOPES = [
  { persona: "default", country: "default", label: "Default (all visitors)" },
  { persona: "retire", country: "default", label: "Retire — any country" },
  { persona: "relocate", country: "default", label: "Relocate — any country" },
  { persona: "default", country: "Spain", label: "Any persona — Spain" },
  { persona: "default", country: "Portugal", label: "Any persona — Portugal" },
  { persona: "default", country: "Italy", label: "Any persona — Italy" },
  { persona: "retire", country: "Spain", label: "Retire · Spain" },
  { persona: "retire", country: "Portugal", label: "Retire · Portugal" },
  { persona: "retire", country: "Italy", label: "Retire · Italy" },
  { persona: "relocate", country: "Spain", label: "Relocate · Spain" },
  { persona: "relocate", country: "Portugal", label: "Relocate · Portugal" },
  { persona: "relocate", country: "Italy", label: "Relocate · Italy" },
];

export function SiteContentPanel({ axios }) {
  const [schemas, setSchemas] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [scope, setScope] = useState({ persona: "default", country: "default" });
  const [loadingSchemas, setLoadingSchemas] = useState(true);

  // Load schemas + overrides once
  useEffect(() => {
    let mounted = true;
    setLoadingSchemas(true);
    Promise.all([
      axios.get("/admin/site-content/schemas"),
      axios.get("/admin/site-content/overrides"),
    ])
      .then(([s, o]) => {
        if (!mounted) return;
        setSchemas(s.data.schemas || []);
        setOverrides(o.data.items || []);
        if (!selectedKey && s.data.schemas?.length) {
          setSelectedKey(s.data.schemas[0].key);
        }
      })
      .catch(() => toast.error("Failed to load site content."))
      .finally(() => mounted && setLoadingSchemas(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axios]);

  const refreshOverrides = () => {
    axios
      .get("/admin/site-content/overrides")
      .then((o) => setOverrides(o.data.items || []))
      .catch(() => {});
  };

  const groupedSchemas = useMemo(() => {
    const m = {};
    schemas.forEach((s) => {
      const g = s.group || "Other";
      (m[g] = m[g] || []).push(s);
    });
    return m;
  }, [schemas]);

  const selectedSchema = useMemo(
    () => schemas.find((s) => s.key === selectedKey),
    [schemas, selectedKey]
  );

  const allowedScopes = useMemo(() => {
    if (!selectedSchema) return SCOPES;
    return SCOPES.filter((s) => {
      if (s.persona === "default" && s.country === "default") return true;
      if (!selectedSchema.supports_persona && s.persona !== "default") return false;
      if (!selectedSchema.supports_country && s.country !== "default") return false;
      return true;
    });
  }, [selectedSchema]);

  // If schema changes and current scope is not allowed, reset to default
  useEffect(() => {
    if (!selectedSchema) return;
    const allowed = allowedScopes.some(
      (s) => s.persona === scope.persona && s.country === scope.country
    );
    if (!allowed) setScope({ persona: "default", country: "default" });
  }, [selectedSchema, allowedScopes, scope]);

  const overrideFor = (section_key, persona, country) =>
    overrides.find(
      (o) =>
        o.section_key === section_key &&
        o.persona === persona &&
        o.country === country
    );

  return (
    <div data-testid="site-content-panel" className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
      {/* Sidebar */}
      <aside className="bg-white border border-[#451C25]/10 p-4 max-h-[80vh] overflow-y-auto">
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-4">
          Sections
        </div>
        {loadingSchemas ? (
          <div className="text-[#6B4954] text-sm">Loading...</div>
        ) : (
          <nav className="space-y-5">
            {Object.entries(groupedSchemas).map(([group, items]) => (
              <div key={group}>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#451C25] mb-2">
                  {group}
                </div>
                <ul className="space-y-0.5">
                  {items.map((s) => {
                    const active = s.key === selectedKey;
                    const hasUnpub = overrides.some(
                      (o) => o.section_key === s.key && o.has_unpublished_changes
                    );
                    return (
                      <li key={s.key}>
                        <button
                          data-testid={`sc-section-${s.key}`}
                          onClick={() => setSelectedKey(s.key)}
                          className={`w-full flex items-center justify-between text-left px-3 py-2 text-[13px] transition-colors ${
                            active
                              ? "bg-[#451C25] text-white"
                              : "text-[#451C25] hover:bg-[#F5EFE7]"
                          }`}
                        >
                          <span className="truncate">{s.label}</span>
                          {hasUnpub && (
                            <span
                              title="Unpublished changes"
                              className="ml-2 w-1.5 h-1.5 rounded-full bg-[#451C25] shrink-0"
                            />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        )}
      </aside>

      {/* Editor */}
      <section>
        {!selectedSchema ? (
          <div className="text-[#6B4954] text-sm">Select a section to edit.</div>
        ) : (
          <SectionEditor
            key={`${selectedSchema.key}-${scope.persona}-${scope.country}`}
            axios={axios}
            schema={selectedSchema}
            scope={scope}
            setScope={setScope}
            allowedScopes={allowedScopes}
            overrideFor={overrideFor}
            onChanged={refreshOverrides}
          />
        )}
      </section>
    </div>
  );
}

function SectionEditor({ axios, schema, scope, setScope, allowedScopes, overrideFor, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const isDefaultScope = scope.persona === "default" && scope.country === "default";

  // Load doc when section or scope changes
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`/admin/site-content/${schema.key}`, {
        params: { persona: scope.persona, country: scope.country },
      })
      .then((r) => mounted && setDoc(r.data))
      .catch(() => toast.error("Could not load this section."))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [axios, schema.key, scope.persona, scope.country]);

  const setField = (fieldKey, value) => {
    setDoc((d) => ({
      ...d,
      draft: { ...(d?.draft || {}), [fieldKey]: value },
      has_unpublished_changes: true,
    }));
  };

  const saveDraft = async () => {
    if (!doc) return;
    setBusy(true);
    try {
      const r = await axios.put(`/admin/site-content/${schema.key}`, { draft: doc.draft }, {
        params: { persona: scope.persona, country: scope.country },
      });
      setDoc(r.data);
      toast.success("Draft saved.");
      onChanged();
    } catch {
      toast.error("Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!doc) return;
    setBusy(true);
    try {
      // Save draft first so any unsaved edits go in
      await axios.put(`/admin/site-content/${schema.key}`, { draft: doc.draft }, {
        params: { persona: scope.persona, country: scope.country },
      });
      const r = await axios.post(
        `/admin/site-content/${schema.key}/publish`,
        null,
        { params: { persona: scope.persona, country: scope.country } }
      );
      setDoc(r.data);
      toast.success("Published. Live on the site.");
      onChanged();
    } catch {
      toast.error("Publish failed.");
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!doc) return;
    if (!window.confirm("Discard all unpublished changes for this scope?")) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `/admin/site-content/${schema.key}/discard`,
        null,
        { params: { persona: scope.persona, country: scope.country } }
      );
      setDoc(r.data);
      toast.success("Draft restored.");
      onChanged();
    } catch {
      toast.error("Discard failed.");
    } finally {
      setBusy(false);
    }
  };

  const deleteOverride = async () => {
    if (isDefaultScope) return;
    if (!window.confirm(`Delete this override? Visitors with this persona/country will revert to default.`))
      return;
    setBusy(true);
    try {
      await axios.delete(`/admin/site-content/${schema.key}`, {
        params: { persona: scope.persona, country: scope.country },
      });
      toast.success("Override removed.");
      setScope({ persona: "default", country: "default" });
      onChanged();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="bg-white border border-[#451C25]/10 p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div>
            <h2 className="font-serif text-2xl tracking-tight">{schema.label}</h2>
            <p className="text-[12px] text-[#6B4954] mt-1">
              {schema.fields.length} field{schema.fields.length !== 1 ? "s" : ""} ·
              {schema.supports_persona ? " persona overrides ✓" : " no persona overrides"} ·
              {schema.supports_country ? " country overrides ✓" : " no country overrides"}
            </p>
          </div>
          {doc && (
            <div
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase ${
                doc.has_unpublished_changes
                  ? "bg-[#451C25]/15 text-[#2B1017]"
                  : "bg-[#183129]/15 text-[#0D231B]"
              }`}
              data-testid="sc-status-badge"
            >
              {doc.has_unpublished_changes ? (
                <>
                  <AlertCircle size={12} /> Unpublished changes
                </>
              ) : (
                <>
                  <CircleCheck size={12} /> Published
                </>
              )}
            </div>
          )}
        </div>

        {/* Scope selector */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Globe2 size={13} className="text-[#6B4954]" />
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#6B4954]">
              Scope
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {allowedScopes.map((s) => {
              const active = s.persona === scope.persona && s.country === scope.country;
              const hasOverride = !!overrideFor(schema.key, s.persona, s.country);
              const isDefault = s.persona === "default" && s.country === "default";
              return (
                <button
                  key={`${s.persona}-${s.country}`}
                  data-testid={`sc-scope-${s.persona}-${s.country}`}
                  onClick={() => setScope({ persona: s.persona, country: s.country })}
                  className={`relative inline-flex items-center gap-2 px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase border transition-colors ${
                    active
                      ? "bg-[#451C25] text-white border-[#451C25]"
                      : "text-[#6B4954] border-[#451C25]/20 hover:border-[#451C25]/50"
                  }`}
                >
                  {s.label}
                  {hasOverride && !isDefault && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#451C25] shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Editor */}
      {loading || !doc ? (
        <div className="bg-white border border-[#451C25]/10 p-10 text-center text-[#6B4954]">
          <Loader2 className="animate-spin inline-block mb-2" size={20} />
          <div className="text-sm">Loading content...</div>
        </div>
      ) : (
        <div className="bg-white border border-[#451C25]/10 p-6 space-y-6">
          {schema.fields.map((f) => (
            <FieldRenderer
              key={f.key}
              field={f}
              value={doc.draft?.[f.key]}
              onChange={(v) => setField(f.key, v)}
            />
          ))}
        </div>
      )}

      {/* Sticky actions */}
      <footer className="sticky bottom-4 z-10 bg-[#F5EFE7] border border-[#451C25]/15 p-4 flex flex-wrap items-center justify-between gap-3 shadow-lg">
        <div className="flex items-center gap-2 text-[12px] text-[#6B4954]">
          <ChevronRight size={14} />
          Editing <strong className="text-[#451C25]">{schema.label}</strong> · scope
          <code className="text-[11px] bg-white px-2 py-0.5 border border-[#451C25]/10">
            {scope.persona}/{scope.country}
          </code>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="sc-discard"
            onClick={discard}
            disabled={busy || !doc?.has_unpublished_changes}
            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase text-[#6B4954] border border-[#451C25]/20 hover:border-[#451C25]/50 disabled:opacity-40"
          >
            <Undo2 size={13} /> Discard
          </button>
          {!isDefaultScope && (
            <button
              data-testid="sc-delete-override"
              onClick={deleteOverride}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase text-[#2B1017] border border-[#2B1017]/30 hover:bg-[#2B1017] hover:text-white disabled:opacity-40"
            >
              <Trash2 size={13} /> Remove override
            </button>
          )}
          <button
            data-testid="sc-save-draft"
            onClick={saveDraft}
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2 text-[11px] tracking-[0.22em] uppercase border border-[#451C25] text-[#451C25] hover:bg-[#451C25] hover:text-white disabled:opacity-40"
          >
            <Save size={13} /> Save draft
          </button>
          <button
            data-testid="sc-publish"
            onClick={publish}
            disabled={busy}
            className="inline-flex items-center gap-2 px-5 py-2 text-[11px] tracking-[0.22em] uppercase bg-[#451C25] text-white hover:bg-[#2B1017] disabled:opacity-40"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />} Publish
          </button>
        </div>
      </footer>
    </div>
  );
}

function FieldRenderer({ field, value, onChange }) {
  if (field.type === "list") return <ListField field={field} value={value} onChange={onChange} />;
  if (field.type === "markdown")
    return (
      <FieldShell label={field.label}>
        <div data-color-mode="light">
          <MDEditor
            value={value || ""}
            onChange={(v) => onChange(v || "")}
            height={220}
            data-testid={`sc-field-${field.key}`}
            preview="edit"
          />
        </div>
      </FieldShell>
    );
  if (field.type === "textarea")
    return (
      <FieldShell label={field.label}>
        <textarea
          data-testid={`sc-field-${field.key}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full border border-[#451C25]/20 px-3 py-2 text-[14px] focus:outline-none focus:border-[#451C25]"
        />
      </FieldShell>
    );
  return (
    <FieldShell label={field.label}>
      <input
        data-testid={`sc-field-${field.key}`}
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#451C25]/20 px-3 py-2 text-[14px] focus:outline-none focus:border-[#451C25]"
      />
    </FieldShell>
  );
}

function FieldShell({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function ListField({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const updateItem = (i, key, val) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it));
    onChange(next);
  };
  const addItem = () => {
    const empty = {};
    field.item_fields.forEach((f) => {
      empty[f.key] = "";
    });
    onChange([...items, empty]);
  };
  const removeItem = (i) => {
    if (!window.confirm("Remove this item?")) return;
    onChange(items.filter((_, idx) => idx !== i));
  };
  const maxItems = field.max_items || 20;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954]">
          {field.label} ({items.length})
        </span>
        {items.length < maxItems && (
          <button
            data-testid={`sc-list-add-${field.key}`}
            onClick={addItem}
            type="button"
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[#451C25] hover:text-[#2B1017]"
          >
            <Plus size={12} /> Add item
          </button>
        )}
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div
            key={i}
            data-testid={`sc-list-item-${field.key}-${i}`}
            className="bg-[#F5EFE7] border border-[#451C25]/10 p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#6B4954]">
                Item {i + 1}
              </span>
              <button
                onClick={() => removeItem(i)}
                type="button"
                className="text-[#6B4954] hover:text-[#2B1017]"
                data-testid={`sc-list-remove-${field.key}-${i}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
            {field.item_fields.map((sub) => (
              <SimpleField
                key={sub.key}
                field={sub}
                value={it?.[sub.key]}
                onChange={(v) => updateItem(i, sub.key, v)}
              />
            ))}
          </div>
        ))}
        {items.length === 0 && (
          <div className="border border-dashed border-[#451C25]/15 p-6 text-center text-[12px] text-[#6B4954]">
            No items yet. Click <strong>Add item</strong> to create the first one.
          </div>
        )}
      </div>
    </div>
  );
}

// SimpleField — non-recursive renderer used inside list items.
// Supports text / textarea / markdown only (lists inside lists are not supported by design).
function SimpleField({ field, value, onChange }) {
  if (field.type === "markdown") {
    return (
      <FieldShell label={field.label}>
        <div data-color-mode="light">
          <MDEditor
            value={value || ""}
            onChange={(v) => onChange(v || "")}
            height={180}
            data-testid={`sc-field-${field.key}`}
            preview="edit"
          />
        </div>
      </FieldShell>
    );
  }
  if (field.type === "textarea") {
    return (
      <FieldShell label={field.label}>
        <textarea
          data-testid={`sc-field-${field.key}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full border border-[#451C25]/20 px-3 py-2 text-[14px] focus:outline-none focus:border-[#451C25]"
        />
      </FieldShell>
    );
  }
  return (
    <FieldShell label={field.label}>
      <input
        data-testid={`sc-field-${field.key}`}
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#451C25]/20 px-3 py-2 text-[14px] focus:outline-none focus:border-[#451C25]"
      />
    </FieldShell>
  );
}
